import {
  blockedReason,
  isCancelled,
  MAIL_SHIFT_STATES,
  MANUAL_TEMPLATE_KEYS,
  mailboxUnits,
  type MailTemplateKey,
  type ManualTemplateKey,
} from '@reditelji/shared';
import type { MailSendState } from '../api/mail';
import type { Shift } from '../api/types';
import type { VolunteerRow } from '../api/volunteers';

export interface TemplateState {
  key: ManualTemplateKey;
  /** When this template last went to this person, or null. */
  sentAt: string | null;
  sentBy: string;
  /** Why the send is unavailable right now, in the coordinator's words. */
  blocked: string | null;
  /** Sent, but the schedule has moved since — only the napotnica can be stale. */
  stale: boolean;
}

export type SendStates = Record<ManualTemplateKey, TemplateState>;

/**
 * Shifts per volunteer, counted the way the mail rules count them.
 *
 * A proposed fill is a coordinator's draft: the server's `briefingShifts` does
 * not see it, so neither does this. Counting it here once made the UI offer a
 * napotnica the server then refused with "brez izmen".
 */
const countsForMail = (state: string) => (MAIL_SHIFT_STATES as readonly string[]).includes(state);

/** One person's shifts, counted the way the mail rules count them. */
export function mailShiftCount(shifts: Pick<Shift, 'state'>[]): number {
  return shifts.filter((s) => countsForMail(s.state)).length;
}

export function mailShiftCounts(shifts: Shift[] | undefined): Map<string, number> {
  const counts = new Map<string, number>();
  for (const s of shifts ?? []) {
    if (!s.volunteerId || !countsForMail(s.state)) continue;
    counts.set(s.volunteerId, (counts.get(s.volunteerId) ?? 0) + 1);
  }
  return counts;
}

export function sendStatesFor(
  v: VolunteerRow,
  sends: MailSendState[],
  shiftCount: number,
): SendStates {
  const mine = new Map(
    sends.filter((s) => s.volunteerId === v.id).map((s) => [s.templateKey, s] as const),
  );
  // The rule itself lives in packages/shared, so the button a coordinator sees
  // and the answer the server gives cannot drift apart.
  const stale =
    v.briefingSentAt !== null && (v.briefingVersion ?? 0) < v.scheduleVersion;
  const eligibility = {
    // The rule lives in packages/shared, so a greyed button and the server's
    // refusal cannot drift apart (SPEC §1.1J).
    isExternal: v.externalGroupId !== null,
    isCancelled: isCancelled(v),
    hasEmail: Boolean(v.email),
    shiftCount,
    // A silently published schedule is not a baseline the change notice can
    // diff against — the server counts mail rows only, so the button must too.
    hasBriefing: v.briefingMailedAt !== null,
    // The UI has no diff, only the schedule version — which moves on a material
    // change. Stricter than the server's answer, never looser, so a greyed
    // button is always honest.
    hasChanges: stale,
  };
  const out = {} as SendStates;
  for (const key of MANUAL_TEMPLATE_KEYS) {
    const sent = mine.get(key);
    out[key] = {
      key,
      sentAt: sent?.at ?? null,
      sentBy: sent?.by ?? '',
      blocked: blockedReason(key, eligibility),
      stale: key === 'napotnica' && stale,
    };
  }
  return out;
}

/**
 * The shared mailboxes inside a selection, as the names that will share one
 * mail — the same grouping the batch makes, so a confirmation promises
 * exactly what the report will say.
 */
export function householdsIn(selected: VolunteerRow[]): string[][] {
  const units = mailboxUnits(selected);
  const byUnit = new Map<string, string[]>();
  for (const v of selected) {
    const unit = units.get(v.id);
    if (unit) byUnit.set(unit, [...(byUnit.get(unit) ?? []), v.name]);
  }
  return [...byUnit.values()].map((names) => names.sort((a, b) => a.localeCompare(b, 'sl')));
}

/** How many mails a selection makes: one per shared mailbox, one per everybody else. */
export function mailCount(selected: VolunteerRow[]): number {
  const units = mailboxUnits(selected);
  return new Set(selected.map((v) => units.get(v.id) ?? `solo:${v.id}`)).size;
}

/** "12. avg" — dense enough for a card row, unambiguous within a season. */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('sl-SI', { day: 'numeric', month: 'short' });
}

export function countSent(key: MailTemplateKey, sends: MailSendState[]): number {
  return sends.filter((s) => s.templateKey === key).length;
}

export function lastSent(key: MailTemplateKey, sends: MailSendState[]): string | null {
  const times = sends.filter((s) => s.templateKey === key).map((s) => s.at);
  return times.length === 0 ? null : times.reduce((a, b) => (a > b ? a : b));
}
